/**
 * @file hooks/useFileMetas.ts
 * @description 批量解析 fileId → 元信息。
 *
 * 阶段 3 完整版：
 *   - 正数 ID：先查 cache_media（status=cached 或 uploaded）；命中即同步返回。
 *     未命中才调 POST /api/files/meta-batch（服务器路径）。
 *   - 负数 ID：从 cache_media 的 'pending:' 行读 filename/size/mime（离线暂存）。
 *
 * 离线状态下命中 cache_media 的元信息直接返回；调用方（form fill page）应做
 * staleTime 长一些避免没必要的 refetch。
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { getMeta as getPendingMeta } from '@/pwa/pending-uploads';
import { getOfflineDb } from '@/pwa/db';
import type { UploadedFileMeta } from '@zhcb/forms/browser';

interface MetaBatchResponse {
  items: Array<{
    id: number;
    filename: string;
    sizeBytes: number;
    mimeType: string;
    deleted: boolean;
  }>;
}

/**
 * 在 cache_media 中查正数 fileId 对应的本地缓存行（cached 或 uploaded）。
 * 未命中返回 undefined。
 */
async function getLocalMetaForRealId(fileId: number): Promise<UploadedFileMeta | undefined> {
  const db = await getOfflineDb();
  // 1) 'file:<id>' 命名空间（stage 3 预下载）
  const cached = await db.get('cache_media', `file:${fileId}`);
  if (cached) {
    return {
      id: fileId,
      filename: cached.filename,
      sizeBytes: cached.sizeBytes,
      mimeType: cached.mimeType,
    };
  }
  // 2) by-uploaded 索引（stage 2 离线上传成功后的 'pending:' 行）
  const uploaded = await db.getAllFromIndex('cache_media', 'by-uploaded', fileId);
  const first = uploaded[0];
  if (first) {
    return {
      id: fileId,
      filename: first.filename,
      sizeBytes: first.sizeBytes,
      mimeType: first.mimeType,
    };
  }
  return undefined;
}

export function useFileMetas(ids: number[]): {
  metas: Map<number, UploadedFileMeta>;
  isLoading: boolean;
} {
  // ── ids 规范化：去重 + 排序 → 稳定字符串 key ──
  // 调用方（表单填写页）每次 render 传入的 ids 都是新数组（collectFileIds 的产物）。
  // 若直接 useMemo([ids])，因 ids 引用每帧都变 → positiveIds/negativeIds 每帧重算并换引用，
  // 连锁导致下方 effect / localMetas / metas / 宿主 uploadHandlers 每次 keystroke 都换引用，
  // 打穿 FieldFactory / ChecklistRow 的 React.memo，造成整树重渲染卡顿。
  // 改用「内容字符串」当依赖：内容不变则数组引用恒定，keystroke 不再触发连锁。
  const sortedKey = Array.from(new Set(ids))
    .sort((a, b) => a - b)
    .join(',');
  const positiveIds = useMemo(
    () =>
      sortedKey
        ? sortedKey
            .split(',')
            .map(Number)
            .filter((id) => id > 0)
        : [],
    [sortedKey],
  );
  const negativeIds = useMemo(
    () =>
      sortedKey
        ? sortedKey
            .split(',')
            .map(Number)
            .filter((id) => id < 0)
        : [],
    [sortedKey],
  );

  // 本地：正数 + 负数都解析
  const [localMetas, setLocalMetas] = useState<Map<number, UploadedFileMeta>>(new Map());
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next = new Map<number, UploadedFileMeta>();
      for (const id of positiveIds) {
        const meta = await getLocalMetaForRealId(id);
        if (meta) next.set(id, meta);
      }
      for (const id of negativeIds) {
        const meta = await getPendingMeta(id);
        if (meta) next.set(id, meta);
      }
      if (!cancelled) setLocalMetas(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [positiveIds, negativeIds]);

  // 仅对本地未命中的正数 ID 走服务器 meta-batch
  const missingPositiveIds = useMemo(
    () => positiveIds.filter((id) => !localMetas.has(id)),
    [positiveIds, localMetas],
  );

  const { data, isLoading } = useQuery<MetaBatchResponse>({
    queryKey: ['file-metas', missingPositiveIds],
    queryFn: () => apiClient.post('/api/files/meta-batch', { ids: missingPositiveIds }),
    enabled: missingPositiveIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // 合并 localMetas + 服务器结果
  const metas = useMemo(() => {
    const merged = new Map<number, UploadedFileMeta>(localMetas);
    if (data?.items) {
      for (const it of data.items) {
        merged.set(it.id, {
          id: it.id,
          filename: it.deleted ? `${it.filename}（已刪除）` : it.filename,
          sizeBytes: it.sizeBytes,
          mimeType: it.mimeType,
        });
      }
    }
    return merged;
  }, [data, localMetas]);

  return { metas, isLoading };
}
